import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthService } from './auth.service';
import { API_CONFIG } from '../constants/api.constants';
import { CallCenterDatabaseTableData } from '../interfaces/call-center-database.interface';
import {
  ExcelUploadResponse,
  ExcelPreviewResponse,
  DatabaseField,
  DatabaseFieldsResponse,
  DataUploadRequest,
  DataUploadResponse,
  DatabaseDataParams,
  DatabaseDataResponse,
  MainActivitiesResponse,
  StatusUpdateRequest,
  StatusUpdateResponse,
  AddOfficialRequest,
  AddOfficialResponse,
  ValidStatusUpdateRequest,
  ValidStatusUpdateResponse,
  ContactValueUpdateRequest,
  ContactValueUpdateResponse,
  AddPhoneRequest,
  AddPhoneResponse,
  AddEmailRequest,
  AddEmailResponse,
  AssignCompaniesRequest,
  AssignCompaniesResponse,
  RemoveAssignmentsResponse,
  CallableRecordsCountResponse
} from '../interfaces/upload.interface';
import { DuplicatePhoneResponse } from '../interfaces/input-dialog.interface';
import { StatusReportResponse } from '../interfaces/status-report.interface';

@Injectable({
  providedIn: 'root'
})
export class UploadService {
  private apiUrl = `${API_CONFIG.BASE_URL}/upload`;
  private databaseApiUrl = `${API_CONFIG.BASE_URL}/database`;
  private customersApiUrl = `${API_CONFIG.BASE_URL}/customers`;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('token');
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });
  }

  getDatabaseFields(): Observable<DatabaseFieldsResponse> {
    return this.http.get<DatabaseFieldsResponse>(
      `${this.apiUrl}/database/fields`,
      { headers: this.getHeaders() }
    );
  }

  uploadExcelFile(file: File): Observable<ExcelUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<ExcelUploadResponse>(
      `${this.apiUrl}/excel`, 
      formData, 
      { headers: this.getHeaders() }
    );
  }

  previewExcelFile(file: File): Observable<ExcelPreviewResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<ExcelPreviewResponse>(
      `${this.apiUrl}/excel/preview`, 
      formData, 
      { headers: this.getHeaders() }
    );
  }

  uploadProcessedData(data: DataUploadRequest): Observable<DataUploadResponse> {
    return this.http.post<DataUploadResponse>(
      `${this.apiUrl}/data`,
      data,
      { headers: this.getHeaders() }
    );
  }

  getDatabaseData(params?: DatabaseDataParams): Observable<DatabaseDataResponse> {
    let httpParams = new HttpParams();
    
    if (params) {
      httpParams = httpParams.set('page', params.page.toString());
      httpParams = httpParams.set('page_size', params.pageSize.toString());
      
      if (params.search) {
        httpParams = httpParams.set('search', params.search);
      }
      if (params.status && params.status.length > 0) {
        params.status.forEach(s => httpParams = httpParams.append('status', s));
      }
      if (params.assignee && params.assignee.length > 0) {
        params.assignee.forEach(a => httpParams = httpParams.append('assignee', a));
      }
      if (params.mainActivity && params.mainActivity.length > 0) {
        params.mainActivity.forEach(a => httpParams = httpParams.append('main_activity', a));
      }
      if (params.dateFrom) {
        httpParams = httpParams.set('date_from', params.dateFrom);
      }
      if (params.dateTo) {
        httpParams = httpParams.set('date_to', params.dateTo);
      }
      if (params.sortBy) {
        httpParams = httpParams.set('sort_by', params.sortBy);
      }
      if (params.sortDir) {
        httpParams = httpParams.set('sort_dir', params.sortDir);
      }
    }
    
    return this.http.get<DatabaseDataResponse>(
      `${this.databaseApiUrl}/data`,
      { headers: this.getHeaders(), params: httpParams }
    );
  }

  getMainActivities(q: string = '', limit: number = 20): Observable<MainActivitiesResponse> {
    let httpParams = new HttpParams().set('limit', limit.toString());
    if (q && q.trim()) {
      httpParams = httpParams.set('q', q.trim());
    }

    return this.http.get<MainActivitiesResponse>(
      `${this.databaseApiUrl}/main-activities`,
      { headers: this.getHeaders(), params: httpParams }
    );
  }

  getStatusReport(): Observable<StatusReportResponse> {
    return this.http.get<StatusReportResponse>(
      `${this.databaseApiUrl}/status-report`,
      { headers: this.getHeaders() }
    );
  }

  exportStatusReport(): Observable<Blob> {
    return this.http.get(
      `${this.databaseApiUrl}/status-report/export`,
      { headers: this.getHeaders(), responseType: 'blob' }
    );
  }

  getSingleCompanyData(companyId: number): Observable<{success: boolean, data: any}> {
    return this.http.get<{success: boolean, data: any}>(
      `${this.apiUrl.replace('/upload', '/database')}/company/${companyId}`,
      { headers: this.getHeaders() }
    );
  }

  getCompanyInteractions(companyId: number): Observable<any> {
    return this.http.get(
      `${API_CONFIG.BASE_URL}/interactions/company/${companyId}`,
      { headers: this.getHeaders() }
    );
  }

  createInteraction(interaction: any): Observable<any> {
    return this.http.post(
      `${API_CONFIG.BASE_URL}/interactions/`,
      interaction,
      { headers: this.getHeaders() }
    );
  }

  updateCompanyStatus(companyId: number, statusData: StatusUpdateRequest): Observable<StatusUpdateResponse> {
    return this.http.put<StatusUpdateResponse>(
      `${this.databaseApiUrl}/company/${companyId}/status`,
      statusData,
      { headers: this.getHeaders() }
    );
  }

  addCompanyOfficial(companyId: number, officialData: AddOfficialRequest): Observable<AddOfficialResponse> {
    return this.http.post<AddOfficialResponse>(
      `${this.databaseApiUrl}/company/${companyId}/official`,
      officialData,
      { headers: this.getHeaders() }
    );
  }

  updateCompanyOfficialName(companyId: number, oldName: string, newName: string): Observable<any> {
    return this.http.put<any>(
      `${this.databaseApiUrl}/company/${companyId}/official`,
      { old_name: oldName, new_name: newName },
      { headers: this.getHeaders() }
    );
  }

  addCompanyPhone(companyId: number, phoneData: AddPhoneRequest): Observable<AddPhoneResponse> {
    return this.http.post<AddPhoneResponse>(
      `${this.databaseApiUrl}/company/${companyId}/phone`,
      phoneData,
      { headers: this.getHeaders() }
    );
  }

  checkDuplicatePhones(phones: string[], excludeCompanyId: number): Observable<DuplicatePhoneResponse> {
    return this.http.post<DuplicatePhoneResponse>(
      `${this.databaseApiUrl}/check-duplicate-phones`,
      { phones, exclude_company_id: excludeCompanyId },
      { headers: this.getHeaders() }
    );
  }

  addCustomerPhone(customerId: number, phoneData: AddPhoneRequest): Observable<AddPhoneResponse> {
    return this.http.post<AddPhoneResponse>(
      `${this.customersApiUrl}/individual/${customerId}/phone`,
      phoneData,
      { headers: this.getHeaders() }
    );
  }

  addCustomerEmail(customerId: number, emailData: AddEmailRequest): Observable<AddEmailResponse> {
    return this.http.post<AddEmailResponse>(
      `${this.customersApiUrl}/individual/${customerId}/email`,
      emailData,
      { headers: this.getHeaders() }
    );
  }

  addCompanyEmail(companyId: number, emailData: AddEmailRequest): Observable<AddEmailResponse> {
    return this.http.post<AddEmailResponse>(
      `${this.databaseApiUrl}/company/${companyId}/email`,
      emailData,
      { headers: this.getHeaders() }
    );
  }

  updateContactValidStatus(companyId: number, validStatusData: ValidStatusUpdateRequest): Observable<ValidStatusUpdateResponse> {
    return this.http.put<ValidStatusUpdateResponse>(
      `${this.databaseApiUrl}/company/${companyId}/validstatus`,
      validStatusData,
      { headers: this.getHeaders() }
    );
  }

  updateCustomerContactValidStatus(customerId: number, validStatusData: ValidStatusUpdateRequest): Observable<ValidStatusUpdateResponse> {
    return this.http.put<ValidStatusUpdateResponse>(
      `${this.customersApiUrl}/individual/${customerId}/validstatus`,
      validStatusData,
      { headers: this.getHeaders() }
    );
  }

  updateContactValue(companyId: number, contactData: ContactValueUpdateRequest): Observable<ContactValueUpdateResponse> {
    return this.http.put<ContactValueUpdateResponse>(
      `${this.databaseApiUrl}/company/${companyId}/contact-value`,
      contactData,
      { headers: this.getHeaders() }
    );
  }

  updateCustomerContactValue(customerId: number, contactData: ContactValueUpdateRequest): Observable<ContactValueUpdateResponse> {
    return this.http.put<ContactValueUpdateResponse>(
      `${this.customersApiUrl}/individual/${customerId}/contact-value`,
      contactData,
      { headers: this.getHeaders() }
    );
  }

  assignCompaniesToEmployee(requestData: AssignCompaniesRequest): Observable<AssignCompaniesResponse> {
    return this.http.post<AssignCompaniesResponse>(
      `${this.databaseApiUrl}/assign-companies`,
      requestData,
      { headers: this.getHeaders() }
    );
  }

  removeCompanyAssignments(companyIds: number[]): Observable<RemoveAssignmentsResponse> {
    return this.http.post<RemoveAssignmentsResponse>(
      `${this.databaseApiUrl}/remove-assignments`,
      { company_ids: companyIds },
      { headers: this.getHeaders() }
    );
  }

  /**
   * Get the count of callable records (records with "hívandó" status)
   */
  getCallableRecordsCount(): Observable<CallableRecordsCountResponse> {
    return this.http.get<CallableRecordsCountResponse>(`${this.databaseApiUrl}/stats/hivando-count`, { headers: this.getHeaders() });
  }
}

